<template>
  <div class="id-page" v-loading="loading">

    <div class="id-header">
      <div class="id-inner id-header-inner">
        <button class="id-back" @click="$router.push('/invoices')">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          Invoices
        </button>
        <div class="id-header-actions" v-if="invoice">
          <button v-if="invoice.pdf_path" class="id-btn id-btn--outline" @click="downloadPdf" :disabled="pdfLoading">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/></svg>
            {{ pdfLoading ? 'Loading…' : 'Download PDF' }}
          </button>
        </div>
      </div>
    </div>

    <div class="id-inner id-body" v-if="invoice">

      <!-- LEFT: sticky info panel -->
      <aside class="id-side">

        <!-- Identity card -->
        <div class="id-side-card">
          <div class="id-side-number">{{ invoice.invoice_number }}</div>
          <div class="id-side-customer">{{ invoice.customer_name }}</div>
          <span class="id-badge id-side-badge" :class="statusClass(invoice.status)">{{ statusLabel(invoice.status) }}</span>

          <!-- Outstanding callout -->
          <div class="id-outstanding" v-if="outstanding > 0 && invoice.status !== 'cancelled'">
            <div class="id-outstanding-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <div>
              <div class="id-outstanding-amount">{{ currency }} {{ fmt(outstanding) }} outstanding</div>
              <div class="id-outstanding-sub">{{ isOverdue ? 'This invoice is overdue. Please arrange payment.' : 'Payment is pending.' }}</div>
            </div>
          </div>
          <div class="id-paid-callout" v-else-if="invoice.status === 'paid'">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            <span>Fully paid</span>
          </div>
        </div>

        <!-- Details list -->
        <div class="id-side-card id-side-details">
          <div class="id-side-head">Details</div>
          <div class="id-side-body">
            <div class="id-detail-row">
              <span class="id-detail-key">Period</span>
              <span class="id-detail-val">{{ fmtDate(invoice.period_start) }} – {{ fmtDate(invoice.period_end) }}</span>
            </div>
            <div class="id-detail-row">
              <span class="id-detail-key">Issue Date</span>
              <span class="id-detail-val">{{ fmtDate(invoice.created_at) }}</span>
            </div>
            <div class="id-detail-row">
              <span class="id-detail-key">Due Date</span>
              <span class="id-detail-val" :class="isOverdue ? 'id-overdue' : ''">
                {{ invoice.due_date ? fmtDate(invoice.due_date) : '—' }}
                <span v-if="isOverdue" class="id-overdue-tag">Overdue</span>
              </span>
            </div>
            <div class="id-detail-row">
              <span class="id-detail-key">Grand Total</span>
              <span class="id-detail-val id-detail-val--strong">{{ currency }} {{ fmt(invoice.total) }}</span>
            </div>
            <div class="id-detail-row">
              <span class="id-detail-key">Paid</span>
              <span class="id-detail-val id-detail-val--paid">{{ currency }} {{ fmt(invoice.amount_paid) }}</span>
            </div>
            <div class="id-detail-row">
              <span class="id-detail-key">Orders</span>
              <span class="id-detail-val">{{ invoice.items?.length ?? 0 }}</span>
            </div>
            <div class="id-side-notes" v-if="invoice.notes">
              <div class="id-side-notes-label">Notes</div>
              <div class="id-side-notes-text">{{ invoice.notes }}</div>
            </div>
          </div>
        </div>
      </aside>

      <!-- RIGHT: line items + payments -->
      <div class="id-main">

      <!-- Line items -->
      <div class="id-section">
        <div class="id-section-title">Line Items</div>
        <div class="id-items-table">
          <div class="id-items-head">
            <div class="id-items-th id-items-th--route">Route</div>
            <div class="id-items-th id-items-th--right">Amount</div>
            <div class="id-items-th id-items-th--right">VAT</div>
            <div class="id-items-th id-items-th--right">Line Total</div>
          </div>
          <div class="id-items-body">
            <div class="id-items-row" v-for="item in invoice.items" :key="item.id">
              <div class="id-items-td id-items-td--route">
                <span class="id-order-ref">{{ item.order_ref }}</span>
                <!-- Shown exactly when the TMS prints it on the invoice PDF: the
                     client has an active Manifest rule (show_manifest). Under
                     the ref, as in the email and on the verify page, rather
                     than a column of its own: a fifth column pushed Line Total
                     off the card on a phone. -->
                <span v-if="invoice.show_manifest" class="id-manifest">
                  <span class="id-manifest-lbl">Manifest No.</span>
                  <span v-if="String(item.manifest_no ?? '').trim()">{{ String(item.manifest_no).trim() }}</span>
                  <span v-else class="id-muted">—</span>
                </span>
                <span class="id-route">{{ item.description?.replace(item.order_ref + ' · ', '') }}</span>
              </div>
              <div class="id-items-td id-items-td--right">{{ currency }} {{ fmt(item.amount) }}</div>
              <div class="id-items-td id-items-td--right">
                <template v-if="item.apply_vat">
                  <span class="id-vat-badge">{{ Number(item.vat_rate).toFixed(0) }}%</span>
                  {{ currency }} {{ fmt(item.vat_amount) }}
                </template>
                <span v-else class="id-muted">—</span>
              </div>
              <div class="id-items-td id-items-td--right id-items-td--bold">{{ currency }} {{ fmt(item.line_total) }}</div>
            </div>
          </div>
          <div class="id-items-totals">
            <div class="id-items-total-row">
              <span>Subtotal</span>
              <span>{{ currency }} {{ fmt(invoice.subtotal) }}</span>
            </div>
            <div class="id-items-total-row">
              <span>VAT</span>
              <span>{{ currency }} {{ fmt(invoice.vat_total) }}</span>
            </div>
            <div class="id-items-total-row id-items-total-row--grand">
              <span>Grand Total</span>
              <span>{{ currency }} {{ fmt(invoice.total) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Payment history -->
      <div class="id-section" v-if="invoice.payments && invoice.payments.length">
        <div class="id-section-title">Payments</div>
        <div class="id-payments-table">
          <div class="id-payments-head">
            <div class="id-payments-th">Date</div>
            <div class="id-payments-th">Method</div>
            <div class="id-payments-th">Reference</div>
            <div class="id-payments-th id-payments-th--right">Amount</div>
          </div>
          <div
            v-for="p in invoice.payments"
            :key="p.id"
            class="id-payments-row"
            :class="isVoided(p) ? 'id-payments-row--voided' : ''"
          >
            <div class="id-payments-td">{{ fmtDate(p.payment_date) }}</div>
            <div class="id-payments-td">{{ p.method?.replace(/_/g, ' ') }}</div>
            <div class="id-payments-td id-muted">{{ p.reference || '—' }}</div>
            <div class="id-payments-td id-payments-td--right">
              <span :class="isVoided(p) ? 'id-muted id-strikethrough' : 'id-payment-amount'">{{ currency }} {{ fmt(p.amount) }}</span>
              <span v-if="isVoided(p)" class="id-void-tag">Voided</span>
            </div>
          </div>
        </div>
      </div>

      </div><!-- /id-main -->

    </div>

    <div class="id-inner id-not-found" v-if="!loading && !invoice">
      <p>Invoice not found.</p>
      <button class="id-btn" @click="$router.push('/invoices')">Back to invoices</button>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '../../api/index'
import { message as ElMessage } from '../../utils/message'
import { fmtDate } from '../../utils/date'

const route = useRoute()
const loading = ref(false)
const pdfLoading = ref(false)
const invoice = ref<any>(null)
const currency = 'KES'

const isVoided = (p: any) => Number(p?.voided) === 1

const outstanding = computed(() => {
  if (!invoice.value) return 0
  const paid = (invoice.value.payments ?? []).filter((p: any) => !isVoided(p)).reduce((s: number, p: any) => s + parseFloat(p.amount || 0), 0)
  return Math.max(0, parseFloat(invoice.value.total || 0) - paid)
})

const isOverdue = computed(() => {
  if (!invoice.value || !invoice.value.due_date) return false
  return outstanding.value > 0 && invoice.value.due_date < new Date().toISOString().slice(0, 10) && !['paid', 'cancelled'].includes(invoice.value.status)
})

async function load() {
  loading.value = true
  try {
    const { data } = await api.get(`/customer/invoices/${route.params.id}`)
    invoice.value = data.data?.invoice ?? null
  } catch {
    invoice.value = null
  } finally {
    loading.value = false
  }
}

async function downloadPdf() {
  pdfLoading.value = true
  try {
    const { data } = await api.get(`/customer/invoices/${route.params.id}/pdf`)
    const url = data.data?.url
    if (url) window.open(url, '_blank')
    else ElMessage.error('PDF not available.')
  } catch {
    ElMessage.error('Could not load PDF.')
  } finally {
    pdfLoading.value = false
  }
}

function fmt(n: any) {
  return parseFloat(n || 0).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function statusLabel(s: string) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
}

function statusClass(s: string) {
  const map: Record<string, string> = {
    draft: 'id-badge--gray',
    sent: 'id-badge--blue',
    partially_paid: 'id-badge--amber',
    paid: 'id-badge--green',
    cancelled: 'id-badge--red',
  }
  return map[s] ?? 'id-badge--gray'
}

onMounted(load)
</script>

<style scoped>
.id-page { min-height: 100vh; background: #fff; font-family: 'DM Sans', sans-serif; }
.id-inner { max-width: 1280px; margin: 0 auto; padding: 0 32px; box-sizing: border-box; }
.id-header { background: #fff; position: sticky; top: 0; z-index: 10; }
.id-header-inner { display: flex; align-items: center; justify-content: space-between; padding-top: 18px; padding-bottom: 18px; }
.id-back { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 700; color: #64748b; background: none; border: none; cursor: pointer; padding: 0; }
.id-back:hover { color: #0061eb; }
.id-header-actions { display: flex; gap: 10px; }
.id-btn { display: flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; border: none; background: #0061eb; color: #fff; }
.id-btn--outline { background: #fff; color: #0061eb; border: 1.5px solid #0061eb; }
.id-btn:disabled { opacity: .5; cursor: not-allowed; }
.id-body { padding-top: 24px; padding-bottom: 40px; display: grid; grid-template-columns: 300px minmax(0, 1fr); gap: 20px; align-items: start; }
.id-main { display: flex; flex-direction: column; gap: 20px; min-width: 0; }

/* Left sticky info panel */
.id-side { position: sticky; top: 80px; display: flex; flex-direction: column; gap: 16px; }
.id-side-card { background: #fff; border: 1.5px solid #ccdeff; border-radius: 12px; overflow: hidden; }
.id-side-card:first-child { padding: 18px 18px; }
.id-side-number { font-size: 18px; font-weight: 800; color: #0f172a; letter-spacing: -0.4px; }
.id-side-customer { font-size: 12.5px; color: #64748b; margin-top: 4px; line-height: 1.4; }
.id-side-badge { display: inline-block; margin-top: 12px; }
.id-side-head { padding: 9px 14px; background: #f0f6ff; border-bottom: 1.5px solid #ccdeff; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .07em; color: #6b86b8; }
.id-side-body { padding: 6px 14px 10px; }
.id-detail-row { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; padding: 6px 0; border-bottom: 1px solid #f8fafc; font-size: 12.5px; }
.id-detail-row:last-child { border-bottom: none; }
.id-detail-key { color: #94a3b8; font-size: 11.5px; flex-shrink: 0; }
.id-detail-val { font-weight: 700; color: #1e293b; text-align: right; }
.id-detail-val--strong { font-weight: 800; color: #0061eb; font-size: 13.5px; }
.id-detail-val--paid { color: #059669; }
.id-side-notes { margin-top: 8px; padding-top: 8px; border-top: 1px solid #f1f5f9; }
.id-side-notes-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: #94a3b8; margin-bottom: 4px; }
.id-side-notes-text { font-size: 12.5px; color: #475569; line-height: 1.55; white-space: pre-wrap; }

.id-badge { font-size: 11px; font-weight: 800; padding: 4px 12px; border-radius: 20px; }
.id-badge--gray { background: #f1f5f9; color: #475569; }
.id-badge--blue { background: #dbeafe; color: #1d4ed8; }
.id-badge--amber { background: #fef3c7; color: #b45309; }
.id-badge--green { background: #d1fae5; color: #065f46; }
.id-badge--red { background: #fee2e2; color: #b91c1c; }
.id-overdue { color: #dc2626; }
.id-overdue-tag { background: #fee2e2; color: #b91c1c; font-size: 10px; font-weight: 700; padding: 1px 6px; border-radius: 20px; margin-left: 4px; }
.id-outstanding { background: #fff5f5; border: 1.5px solid #fecaca; border-radius: 10px; padding: 12px 14px; display: flex; align-items: center; gap: 12px; margin-top: 14px; }
.id-outstanding-icon { color: #dc2626; flex-shrink: 0; }
.id-outstanding-amount { font-size: 15px; font-weight: 800; color: #dc2626; }
.id-outstanding-sub { font-size: 11.5px; color: #b91c1c; margin-top: 2px; line-height: 1.4; }
.id-paid-callout { display: flex; align-items: center; gap: 8px; margin-top: 14px; padding: 10px 12px; background: #ecfdf5; border: 1.5px solid #a7f3d0; border-radius: 10px; color: #059669; font-size: 13px; font-weight: 700; }
.id-section { background: #fff; border: 1.5px solid #ccdeff; border-radius: 12px; overflow: hidden; }
.id-section-title { font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: .07em; color: #6b86b8; padding: 12px 16px; border-bottom: 1.5px solid #ccdeff; background: #f0f6ff; }
/* The head and every row are separate grids on the same fixed tracks, so a
   column lines up down the whole table whatever its amounts. With flex each
   row sized itself to its own figures and the columns drifted on a phone. */
.id-items-table { container-type: inline-size; }
.id-items-head { display: grid; grid-template-columns: minmax(0, 2fr) repeat(3, minmax(0, 1fr)); background: #f0f6ff; border-bottom: 1.5px solid #ccdeff; }
.id-items-th { min-width: 0; padding: 9px 14px; font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; color: #6b86b8; }
.id-items-th--right { text-align: right; }
.id-items-body .id-items-row { display: grid; grid-template-columns: minmax(0, 2fr) repeat(3, minmax(0, 1fr)); border-bottom: 1px solid #e8f0ff; }
.id-items-body .id-items-row:last-child { border-bottom: none; }
.id-items-td { min-width: 0; padding: 10px 14px; font-size: 12px; color: #334155; }
.id-items-td--route { overflow-wrap: anywhere; }
.id-items-td--right { text-align: right; }
.id-items-td--bold { font-weight: 700; color: #0f172a; }
.id-order-ref { font-weight: 700; color: #0f172a; display: block; font-size: 12px; }
.id-route { font-size: 11px; color: #64748b; display: block; margin-top: 2px; }
.id-manifest { display: block; margin-top: 2px; font-size: 11px; font-weight: 600; color: #0f172a; }
.id-manifest-lbl { font-size: 10px; font-weight: 700; color: #94a3b8; margin-right: 3px; }
.id-vat-badge { background: #dbeafe; color: #1d4ed8; font-size: 9px; font-weight: 700; padding: 1px 4px; border-radius: 3px; margin-right: 4px; }
.id-muted { color: #94a3b8; }
.id-items-totals { border-top: 2px solid #ccdeff; padding: 10px 14px; display: flex; flex-direction: column; gap: 4px; align-items: flex-end; }
.id-items-total-row { display: flex; gap: 48px; font-size: 12px; color: #64748b; }
.id-items-total-row--grand { font-size: 15px; font-weight: 800; color: #0061eb; margin-top: 4px; }
.id-payments-head { display: grid; grid-template-columns: 1fr 1fr 1.5fr 1fr; background: #f0f6ff; border-bottom: 1.5px solid #ccdeff; }
.id-payments-th { padding: 9px 14px; font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; color: #6b86b8; }
.id-payments-th--right { text-align: right; }
.id-payments-row { display: grid; grid-template-columns: 1fr 1fr 1.5fr 1fr; border-bottom: 1px solid #e8f0ff; }
.id-payments-row:last-child { border-bottom: none; }
.id-payments-row--voided { opacity: .5; }
.id-payments-td { padding: 10px 14px; font-size: 12px; color: #334155; }
.id-payments-td--right { text-align: right; }
.id-payment-amount { font-weight: 700; color: #065f46; }
.id-void-tag { background: #fee2e2; color: #b91c1c; font-size: 10px; font-weight: 700; padding: 1px 6px; border-radius: 10px; margin-left: 6px; }
.id-strikethrough { text-decoration: line-through; }
.id-not-found { padding: 60px 0; text-align: center; color: #94a3b8; }
@media (max-width: 760px) {
  .id-body { grid-template-columns: 1fr; }
  .id-side { position: static; top: auto; }
}
/* A narrow card (on a phone, or beside the 300px side panel on a small
   tablet) has no room for four columns of figures: the order and its route
   take the full width and the three amounts sit under it. Measured on the
   card, not the window, because at 768px the side panel leaves the table
   about as little room as a phone does. */
@container (max-width: 459px) {
  .id-items-head, .id-items-body .id-items-row { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .id-items-th--route { display: none; }
  .id-items-td--route { grid-column: 1 / -1; padding-bottom: 2px; }
  .id-items-th, .id-items-td { padding-left: 12px; padding-right: 12px; }
  .id-items-td--right { padding-top: 4px; }
  /* The rate on its own line, so the VAT figure wraps the same way on every row */
  .id-vat-badge { display: table; margin: 0 0 3px auto; }
}
</style>
